/*
 * /prescription/$patientId (page)
 */
import {
	Badge,
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Container,
	Page,
	Placeholder,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
	toast,
} from "@emr/react-ui"
import { useAuth } from "@emr/auth/client"
import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"
import { Check, Pencil, Plus, RefreshCw, X } from "lucide-react"
import { PrescriptionForm } from "../../features/prescription/cannabis/prescription-form"
import { HistoricPrescriptions } from "../../features/prescription/cannabis/historic-prescriptions"
import { TopicalPrescriptionSection } from "../../features/prescription/topical/topical-prescription-section"
import { VaporizerPrescriptionSection } from "../../features/prescription/vaporizer/vaporizer-prescription-section"
import { PatientHeader } from "../../features/patient/patient-header"
import { trpc } from "../../trpc"

export const Route = createFileRoute("/prescription/$patientId")({
	component: PrescriptionPage,
})

const MOCK_PATIENT = {
	firstName: "...",
	lastName: "",
	dateOfBirth: null,
	bloodType: null,
	allergies: null,
	emergencyContactName: null,
	emergencyContactPhone: null,
}

function PrescriptionPage() {
	const { patientId } = Route.useParams()
	const { role } = useAuth()
	const [isDirty, setIsDirty] = useState(false)
	const [isCreatingNew, setIsCreatingNew] = useState(false)

	const isAdmin = role === "admin" || role === "super_admin"

	const regeneratePdf = trpc.prescription.regeneratePdf.useMutation({
		onSuccess: () => toast.success("PDFs regenerated successfully"),
		onError: (e) => toast.error(`PDF regeneration failed: ${e.message}`),
	})

	const { data: patient, isLoading: patientLoading } =
		trpc.patient.getByFilter.useQuery({ where: { id: patientId } })

	// Practitioners see only their own record; admins see all
	const { data: myPractitioner, isLoading: myPracLoading } =
		trpc.practitioner.getByFilter.useQuery(
			{ where: {} },
			{ enabled: !isAdmin },
		)

	const { data: allPractitioners, isLoading: allPracLoading } =
		trpc.practitioner.getAllByFilter.useQuery(
			{ where: { active: true } },
			{ enabled: isAdmin },
		)

	const { data: prescription, isLoading: prescriptionLoading } =
		trpc.prescription.getByFilter.useQuery({
			where: { patientId, isLatest: true, active: true },
		})

	const { data: existingLpPrescriptions, isLoading: lpLoading } =
		trpc.lpPrescription.getAllByFilter.useQuery(
			{ where: { prescriptionId: prescription?.id, active: true } },
			{ enabled: !!prescription?.id },
		)

	const { data: patientDiagnoses, isLoading: patientDiagnosesLoading } =
		trpc.patientDiagnosis.getAllByFilter.useQuery({ where: { patientId, active: true } })

	const { data: allDiagnoses, isLoading: allDiagnosesLoading } =
		trpc.diagnosis.getAllByFilter.useQuery({ where: { active: true } })

	const fullDiagnosisText = (() => {
		if (!patientDiagnoses?.length || !allDiagnoses?.length) return ""
		const diagnosisMap = Object.fromEntries(allDiagnoses.map((d) => [d.id, d.name]))
		const primary = patientDiagnoses.find((pd) => pd.type === "primary")
		const secondaries = patientDiagnoses.filter((pd) => pd.type === "secondary")
		const parts = [
			...(primary ? [diagnosisMap[primary.diagnosisId] ?? ""] : []),
			...secondaries.map((pd) => diagnosisMap[pd.diagnosisId] ?? ""),
		].filter(Boolean)
		return parts.join(", ")
	})()

	const isLoading =
		patientLoading ||
		(isAdmin ? allPracLoading : myPracLoading) ||
		prescriptionLoading ||
		lpLoading ||
		patientDiagnosesLoading ||
		allDiagnosesLoading

	// For practitioner role: their own ID; for admin: null (must select in form)
	const defaultPractitionerId = isAdmin ? null : (myPractitioner?.id ?? null)

	// Admin: build options list; practitioner: no options (auto-set)
	const practitionerOptions = isAdmin
		? (allPractitioners ?? []).map((p) => ({
				label: `${p.firstName} ${p.lastName}${(p as any).professionalDesignation ? `, ${(p as any).professionalDesignation}` : ""}`,
				value: p.id,
			}))
		: undefined

	const displayPatient = patient ?? (MOCK_PATIENT as any)

	function handleNewSuccess() {
		setIsCreatingNew(false)
		setIsDirty(false)
	}

	return (
		<Page centerVertical={false}>
			<PatientHeader patient={displayPatient} patientId={patientId} isDirty={isDirty} />
			<Container width="xl" padding="lg" gap="lg">
				{isLoading ? (
					<Placeholder title="Loading patient data..." />
				) : (
					<Tabs defaultValue="cannabis">
						<TabsList>
							<TabsTrigger value="cannabis">Cannabis Prescription</TabsTrigger>
							<TabsTrigger value="topical">Topical Prescription</TabsTrigger>
							<TabsTrigger value="vaporizer">Vaporizer</TabsTrigger>
							<TabsTrigger value="lp-enrollments">LP Enrollments</TabsTrigger>
						</TabsList>

						{/* ── Cannabis tab ── */}
						<TabsContent value="cannabis" className="flex flex-col gap-4 mt-4">
							{prescription ? (
								<>
									{/* New prescription form — on top when creating */}
									{isCreatingNew && (
										<Card>
											<CardHeader>
												<div className="flex items-center justify-between">
													<div>
														<CardTitle>New Prescription</CardTitle>
														<CardDescription>
															Saving will archive the current prescription and activate this one.
														</CardDescription>
													</div>
													<Button
														size="sm"
														variant="ghost"
														onClick={() => {
															setIsCreatingNew(false)
															setIsDirty(false)
														}}
													>
														<X className="w-4 h-4 mr-1" />
														Cancel
													</Button>
												</div>
											</CardHeader>
											<CardContent>
												<PrescriptionForm
													patientId={patientId}
													practitionerId={defaultPractitionerId}
													practitionerOptions={practitionerOptions}
													autoFillDiagnosis={fullDiagnosisText}
													onDirtyChange={setIsDirty}
													onSuccess={handleNewSuccess}
												/>
											</CardContent>
										</Card>
									)}

									{/* Active prescription — read-only */}
									<Card>
										<CardHeader>
											<div className="flex items-center justify-between">
												<div>
													<CardTitle>Active Prescription</CardTitle>
													{prescription.startDate && (
														<CardDescription>
															Started{" "}
															{new Date(prescription.startDate).toLocaleDateString("en-CA", {
																year: "numeric",
																month: "short",
																day: "numeric",
															})}
															{prescription.endDate && (
																<>
																	{" "}· Expires{" "}
																	{new Date(prescription.endDate).toLocaleDateString("en-CA", {
																		year: "numeric",
																		month: "short",
																		day: "numeric",
																	})}
																</>
															)}
														</CardDescription>
													)}
												</div>
												<div className="flex items-center gap-2">
													<Badge variant="secondary">Active</Badge>
													{!isCreatingNew && (
														<Button
															size="sm"
															variant="outline"
															onClick={() => setIsCreatingNew(true)}
														>
															<Plus className="w-4 h-4 mr-1" />
															New Prescription
														</Button>
													)}
													{isAdmin && (
														<Button
															size="sm"
															variant="ghost"
															disabled={regeneratePdf.isPending}
															onClick={() => regeneratePdf.mutate({ id: prescription.id })}
															title="Regenerate PDFs"
														>
															<RefreshCw className={`w-4 h-4 ${regeneratePdf.isPending ? "animate-spin" : ""}`} />
														</Button>
													)}
												</div>
											</div>
										</CardHeader>
										<CardContent>
											<PrescriptionForm
												patientId={patientId}
												practitionerId={prescription.practitionerId ?? defaultPractitionerId}
												prescription={prescription}
												existingLpPrescriptions={existingLpPrescriptions ?? undefined}
												disabled={true}
											/>
										</CardContent>
									</Card>
								</>
							) : (
								/* No existing prescription — show create form */
								<Card>
									<CardHeader>
										<CardTitle>New Prescription</CardTitle>
									</CardHeader>
									<CardContent>
										<PrescriptionForm
											patientId={patientId}
											practitionerId={defaultPractitionerId}
											practitionerOptions={practitionerOptions}
											autoFillDiagnosis={fullDiagnosisText}
											onDirtyChange={setIsDirty}
										/>
									</CardContent>
								</Card>
							)}
							<HistoricPrescriptions patientId={patientId} />
						</TabsContent>

						{/* ── Topical tab ── */}
						<TabsContent value="topical" className="mt-4">
							<Card>
								<CardContent className="py-6">
									<TopicalPrescriptionSection patientId={patientId} />
								</CardContent>
							</Card>
						</TabsContent>

						{/* ── Vaporizer tab ── */}
						<TabsContent value="vaporizer" className="mt-4">
							<Card>
								<CardContent className="py-6">
									<VaporizerPrescriptionSection patientId={patientId} />
								</CardContent>
							</Card>
						</TabsContent>

						{/* ── LP Enrollments tab ── */}
						<TabsContent value="lp-enrollments" className="mt-4">
							<LpEnrollmentsTab patientId={patientId} />
						</TabsContent>
					</Tabs>
				)}
			</Container>
		</Page>
	)
}

/* ─── LP Enrollments tab ────────────────────────────────────────────────── */

function LpEnrollmentsTab({ patientId }: { patientId: string }) {
	const [addingProducerId, setAddingProducerId] = useState("")
	const [addingClientId, setAddingClientId]     = useState("")
	const [editingId, setEditingId]               = useState<string | null>(null)
	const [editingValue, setEditingValue]         = useState("")

	const { data: enrollments = [], isLoading, refetch } =
		trpc.patientLpEnrollment.getAllForPatient.useQuery({ patientId })

	const { data: allProducers = [] } = trpc.producer.getAllByFilter.useQuery({ where: { active: true } })
	const cannabisProducers = (allProducers as any[]).filter((p: any) => !p.isTopicalProducer)

	const upsert = trpc.patientLpEnrollment.upsertForPatient.useMutation({
		onSuccess: () => {
			refetch()
			setAddingProducerId("")
			setAddingClientId("")
			setEditingId(null)
			setEditingValue("")
		},
		onError: (e: any) => toast.error(e.message),
	})

	const approve = trpc.patientLpEnrollment.approveClientId.useMutation({
		onSuccess: () => { refetch(); toast.success("LP Client ID approved") },
		onError: (e: any) => toast.error(e.message),
	})

	const reject = trpc.patientLpEnrollment.rejectClientId.useMutation({
		onSuccess: () => { refetch(); toast.success("Pending update rejected") },
		onError: (e: any) => toast.error(e.message),
	})

	function handleAdd() {
		if (!addingProducerId) { toast.error("Select an LP"); return }
		upsert.mutate({ patientId, producerId: addingProducerId, lpClientId: addingClientId.trim() || null })
	}

	function handleEditSave(enrollment: any) {
		upsert.mutate({ patientId, producerId: enrollment.producerId, lpClientId: editingValue.trim() || null })
	}

	if (isLoading) return <Placeholder title="Loading LP enrollments…" />

	const enrolledProducerIds = new Set((enrollments as any[]).map((e: any) => e.producerId))
	const availableToAdd = cannabisProducers.filter((p: any) => !enrolledProducerIds.has(p.id))

	return (
		<div className="flex flex-col gap-4">
			<Card>
				<CardHeader className="pb-3">
					<CardTitle className="text-sm">LP Enrollments</CardTitle>
					<p className="text-xs text-muted-foreground">
						Each LP assigns the patient a unique client ID used on billing reports.
						Patient-proposed changes appear as "Pending" and require your approval.
					</p>
				</CardHeader>
				<CardContent className="flex flex-col gap-3">
					{!(enrollments as any[]).length && (
						<p className="text-sm text-muted-foreground">No LP enrollments yet.</p>
					)}

					{(enrollments as any[]).map((e: any) => (
						<div key={e.id} className="rounded-lg border px-3 py-3 space-y-2">
							{/* LP name + confirmed ID */}
							<div className="flex items-center gap-3">
								<div className="flex-1 min-w-0">
									<p className="text-sm font-medium truncate">{e.producerName ?? "LP"}</p>
									{editingId === e.id ? (
										<div className="flex items-center gap-2 mt-1">
											<input
												autoFocus
												type="text"
												value={editingValue}
												onChange={(ev) => setEditingValue(ev.target.value)}
												placeholder="LP Client ID"
												className="h-7 w-44 rounded-md border bg-background px-2 text-sm"
											/>
											<Button
												size="sm"
												variant="ghost"
												className="px-1.5 h-7"
												disabled={upsert.isPending}
												onClick={() => handleEditSave(e)}
											>
												<Check className="w-3.5 h-3.5" />
											</Button>
											<Button
												size="sm"
												variant="ghost"
												className="px-1.5 h-7"
												onClick={() => setEditingId(null)}
											>
												<X className="w-3.5 h-3.5" />
											</Button>
										</div>
									) : (
										<p className="text-xs text-muted-foreground mt-0.5">
											Client ID:{" "}
											{e.lpClientId
												? <span className="font-mono text-foreground">{e.lpClientId}</span>
												: <span className="italic">not set</span>}
										</p>
									)}
								</div>
								{editingId !== e.id && (
									<Button
										size="sm"
										variant="ghost"
										className="px-2 h-7 shrink-0"
										title="Edit confirmed LP Client ID"
										onClick={() => { setEditingId(e.id); setEditingValue(e.lpClientId ?? "") }}
									>
										<Pencil className="w-3.5 h-3.5" />
									</Button>
								)}
							</div>

							{/* Pending patient update */}
							{e.pendingLpClientId && (
								<div className="rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 px-3 py-2 flex items-center gap-3">
									<div className="flex-1 min-w-0">
										<p className="text-xs font-medium text-amber-700 dark:text-amber-400">
											Patient-proposed update
										</p>
										<p className="text-sm font-mono mt-0.5">{e.pendingLpClientId}</p>
									</div>
									<div className="flex items-center gap-1 shrink-0">
										<Button
											size="sm"
											variant="outline"
											className="h-7 px-2 border-green-500 text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-950/30"
											disabled={approve.isPending}
											onClick={() => approve.mutate({ enrollmentId: e.id })}
										>
											<Check className="w-3.5 h-3.5 mr-1" />
											Approve
										</Button>
										<Button
											size="sm"
											variant="ghost"
											className="h-7 px-2 text-destructive hover:bg-destructive/10"
											disabled={reject.isPending}
											onClick={() => reject.mutate({ enrollmentId: e.id })}
										>
											<X className="w-3.5 h-3.5 mr-1" />
											Reject
										</Button>
									</div>
								</div>
							)}
						</div>
					))}

					{/* Add new enrollment — only shown when unenrolled LPs exist */}
					{availableToAdd.length > 0 && (
					<div className="border-t pt-3 mt-1">
						<p className="text-xs font-medium text-muted-foreground mb-2">Add LP Enrollment</p>
						<div className="flex flex-wrap items-end gap-2">
							<div className="flex flex-col gap-1">
								<label className="text-xs text-muted-foreground">Licensed Producer</label>
								<select
									value={addingProducerId}
									onChange={(e) => setAddingProducerId(e.target.value)}
									className="h-9 rounded-md border bg-background px-2 text-sm min-w-[180px]"
								>
									<option value="">Select LP…</option>
									{availableToAdd.map((p: any) => (
										<option key={p.id} value={p.id}>{p.name}</option>
									))}
								</select>
							</div>
							<div className="flex flex-col gap-1">
								<label className="text-xs text-muted-foreground">Client ID (optional)</label>
								<input
									type="text"
									value={addingClientId}
									onChange={(e) => setAddingClientId(e.target.value)}
									placeholder="LP-assigned ID"
									className="h-9 rounded-md border bg-background px-2 text-sm w-40"
								/>
							</div>
							<Button
								size="sm"
								onClick={handleAdd}
								disabled={!addingProducerId || upsert.isPending}
							>
								<Plus className="w-3.5 h-3.5 mr-1" />
								Add
							</Button>
						</div>
					</div>
					)}
				</CardContent>
			</Card>
		</div>
	)
}
